/**
 * batch-70 uitrusting-controle (FO-531, spec 231) - de pure regels van de
 * uitrustingscontrole. DB-vrij en zonder klok (`now` komt altijd van buiten):
 * gedeeld door de API (lijstopbouw, compleet-regel, afhandeling), de portal
 * (wizard) en de backoffice (overzicht), zodat scherm en server dezelfde
 * uitkomst geven.
 *
 *  - `bouwControleLijst(bezit, dragers, setsoorten)`: de afvinklijst per
 *    medewerker uit wat de app al weet. Een koffer of kar per lade (module-
 *    onderdelen uitgeklapt, bulk met aantal, een los exemplaar met
 *    serienummer en keuringsinfo), het accugereedschap uit de exemplaren en de
 *    bulk op naam, en per setsoort zonder uitgifte één set "volgens de app nog
 *    niet aan jou uitgegeven". Twee koffers van dezelfde soort zijn twee sets.
 *  - `magGereedschapTekenen(sets)`: de gereedschapsovereenkomst is pas
 *    tekenbaar als de sets compleet zijn (alle regels aanwezig of door het
 *    magazijn afgehandeld als aangevuld, vervangen, n.v.t. of hersteld).
 *  - de mappings van een melding naar de bestaande statussen
 *    (DragerInhoudStatus, VoorraadInhoudAfwijkingType) en de weergave van een
 *    overeenkomst.
 */
import type {
  BruikleenTriggerSoort,
  MiddelBruikleenStatus,
  UitrustingAfhandeling,
  UitrustingControleRegelStatus,
  UitrustingControleSetStatus,
  UitrustingOntbrekendActie,
  UitrustingOvereenkomstActie,
  UitrustingOvereenkomstStatus,
  UitrustingRegelBron,
  UitrustingSetSoort,
} from '@streep/types'
import { UITRUSTING_KEURING_SIGNAAL_DAGEN, UITRUSTING_SET_SOORT_LABELS } from '@streep/types'
import type { DragerInhoudStatusLike } from './compleetheid'

// ─── Invoer van de lijstopbouw ──────────────────────────────────────────────────

/** Een los traceerbaar exemplaar (in een lade of los op naam). */
export interface LijstUniekExemplaar {
  id: string
  artikelId: string
  naam: string
  merk: string | null
  model: string | null
  serienummer: string | null
  assetTag: string | null
  volgendeKeuring: Date | null
  houdbaarheidsdatum: Date | null
}

/** Eén regel in een lade van een kar of koffer. Precies één van de drie bronnen. */
export interface LijstDragerRegel {
  id: string
  ladeNummer: number
  ladeNaam: string | null
  volgorde: number
  aantal: number
  status: DragerInhoudStatusLike
  losArtikel: { id: string; naam: string; merk: string | null } | null
  moduleArtikel: {
    id: string
    naam: string
    code: string | null
    onderdelen: Array<{ id: string; omschrijving: string; aantal: number; losArtikelId: string | null }>
  } | null
  losUniekItem: LijstUniekExemplaar | null
}

export interface LijstDrager {
  dragerId: string
  /** Het exemplaar van de koffer of kar zelf. */
  uniekItemId: string
  reeks: 'KAR' | 'KOF' | 'SK' | 'PBM'
  /** Het type van het pakket waaruit hij is samengesteld (FO-510), of null. */
  pakketType: string | null
  naam: string
  /** Het nummer op de koffer (assetTag: KOF1031, PBM1007). */
  nummer: string | null
  uitgegevenOp: Date | null
  regels: LijstDragerRegel[]
}

/** Een FO-301-servicekoffer zonder lades: de pakketinhoud met de afwijkingen erop. */
export interface LijstPakketKoffer {
  uniekItemId: string
  naam: string
  nummer: string | null
  uitgegevenOp: Date | null
  regels: Array<{ pakketRegelId: string; artikelId: string; naam: string; aantal: number }>
}

/** Een exemplaar los op naam (geen koffer, niet in een lade). */
export interface LijstLosExemplaar extends LijstUniekExemplaar {
  /** Hoort bij het accugereedschap (pakket-template ACCUGEREEDSCHAP, of de trefwoord-terugval). */
  isAccu: boolean
  /** Handgereedschap los op naam (categorie GEREEDSCHAP, geen accu): hoort bij de servicekoffer-stap. */
  isLosGereedschap?: boolean
  uitgegevenOp: Date | null
}

/** Bulk op naam (netto uitgegeven). */
export interface LijstBulk {
  artikelId: string
  naam: string
  aantal: number
  isAccu: boolean
  /** Handgereedschap los op naam (categorie GEREEDSCHAP, geen accu): hoort bij de servicekoffer-stap. */
  isLosGereedschap?: boolean
}

/** De sleutel van de set met het losse gereedschap op naam (geen koffer of kar). */
export const LOS_GEREEDSCHAP_SET_SLEUTEL = 'GEREEDSCHAP_LOS'

export interface LijstBezit {
  uniekeItems: LijstLosExemplaar[]
  bulk: LijstBulk[]
  pakketKoffers: LijstPakketKoffer[]
}

// ─── Uitvoer van de lijstopbouw ─────────────────────────────────────────────────

export interface ControleLijstRegel {
  sleutel: string
  bron: UitrustingRegelBron
  groep: string
  ladeNummer: number | null
  volgorde: number
  omschrijving: string
  detail: string | null
  aantal: number
  serienummer: string | null
  dragerInhoudRegelId: string | null
  moduleOnderdeelId: string | null
  uniekItemId: string | null
  voorraadArtikelId: string | null
  pakketRegelId: string | null
  opNaam: boolean
  volgendeKeuring: Date | null
  houdbaarheidsdatum: Date | null
}

export interface ControleLijstSet {
  sleutel: string
  soort: UitrustingSetSoort
  titel: string
  nummer: string | null
  bekendBijApp: boolean
  dragerId: string | null
  uniekItemId: string | null
  uitgegevenOp: Date | null
  regels: ControleLijstRegel[]
}

/** De setsoort van een kar of koffer: reeks KAR, PBM (of een PBM-pakket), anders de servicekoffer. */
export function setSoortVanDrager(d: Pick<LijstDrager, 'reeks' | 'pakketType'>): UitrustingSetSoort {
  if (d.reeks === 'KAR') return 'KAR'
  if (d.reeks === 'PBM' || d.pakketType === 'PBM_KOFFER') return 'PBM_KOFFER'
  return 'SERVICEKOFFER'
}

/**
 * Terugval voor accugereedschap zolang er geen pakket-template ACCUGEREEDSCHAP
 * is (A6): een trefwoord in de naam, het merk of het model ("accu", "M18",
 * "18V", "lader"). Met een template beslist alleen het template.
 */
export function isAccuArtikelNaam(input: { naam: string; merk?: string | null; model?: string | null }): boolean {
  const tekst = [input.naam, input.merk ?? '', input.model ?? ''].join(' ').toLowerCase()
  // "accu" ook als voorvoegsel: Accuboormachine, Accuzaag, Accupack (review I7).
  if (/(^|[^a-z0-9])accu(?!ra)/.test(tekst)) return true
  return /(^|[^a-z0-9])(li-?ion|m12|m18|m28|xr|10[.,]8\s?v|12\s?v|18\s?v|36\s?v|54\s?v|lader|laders|snellader|acculader)([^a-z0-9]|$)/.test(tekst)
}

const NL_VOLGORDE = (a: string | null, b: string | null): number => (a ?? '').localeCompare(b ?? '', 'nl', { numeric: true })

function ladeGroep(nummer: number, naam: string | null): string {
  return naam && naam.trim() ? `Lade ${nummer} - ${naam.trim()}` : `Lade ${nummer}`
}

function exemplaarDetail(e: Pick<LijstUniekExemplaar, 'merk' | 'model'>): string | null {
  const d = [e.merk, e.model].filter((x): x is string => Boolean(x && x.trim())).join(' ')
  return d || null
}

/** De regels van één kar of koffer: per lade, module-onderdelen uitgeklapt. NVT-regels tellen niet mee. */
function regelsVanDrager(d: LijstDrager): ControleLijstRegel[] {
  const uit: ControleLijstRegel[] = []
  const regels = [...d.regels].sort((a, b) => a.ladeNummer - b.ladeNummer || a.volgorde - b.volgorde || a.id.localeCompare(b.id))
  for (const r of regels) {
    if (r.status === 'NVT') continue
    const groep = ladeGroep(r.ladeNummer, r.ladeNaam)
    const basis = {
      groep,
      ladeNummer: r.ladeNummer,
      volgorde: r.volgorde,
      dragerInhoudRegelId: r.id,
      pakketRegelId: null,
    }
    if (r.losUniekItem) {
      const e = r.losUniekItem
      uit.push({
        ...basis,
        sleutel: `DRAGER_REGEL:${r.id}`,
        bron: 'DRAGER_REGEL',
        omschrijving: e.naam,
        detail: exemplaarDetail(e),
        aantal: 1,
        serienummer: e.serienummer ?? e.assetTag,
        moduleOnderdeelId: null,
        uniekItemId: e.id,
        voorraadArtikelId: e.artikelId,
        // Een exemplaar in een koffer op zijn naam staat (transitief) op zijn naam.
        opNaam: true,
        volgendeKeuring: e.volgendeKeuring,
        houdbaarheidsdatum: e.houdbaarheidsdatum,
      })
      continue
    }
    if (r.moduleArtikel) {
      const m = r.moduleArtikel
      if (m.onderdelen.length === 0) {
        uit.push({
          ...basis,
          sleutel: `DRAGER_REGEL:${r.id}`,
          bron: 'DRAGER_REGEL',
          omschrijving: m.naam,
          detail: m.code,
          aantal: r.aantal,
          serienummer: null,
          moduleOnderdeelId: null,
          uniekItemId: null,
          voorraadArtikelId: m.id,
          opNaam: false,
          volgendeKeuring: null,
          houdbaarheidsdatum: null,
        })
        continue
      }
      for (const o of m.onderdelen) {
        uit.push({
          ...basis,
          sleutel: `MODULE:${r.id}:${o.id}`,
          bron: 'MODULE_ONDERDEEL',
          omschrijving: o.omschrijving,
          detail: m.code ? `${m.naam} (${m.code})` : m.naam,
          aantal: o.aantal * Math.max(1, r.aantal),
          serienummer: null,
          moduleOnderdeelId: o.id,
          uniekItemId: null,
          voorraadArtikelId: o.losArtikelId,
          opNaam: false,
          volgendeKeuring: null,
          houdbaarheidsdatum: null,
        })
      }
      continue
    }
    if (r.losArtikel) {
      uit.push({
        ...basis,
        sleutel: `DRAGER_REGEL:${r.id}`,
        bron: 'DRAGER_REGEL',
        omschrijving: r.losArtikel.naam,
        detail: r.losArtikel.merk,
        aantal: r.aantal,
        serienummer: null,
        moduleOnderdeelId: null,
        uniekItemId: null,
        voorraadArtikelId: r.losArtikel.id,
        opNaam: false,
        volgendeKeuring: null,
        houdbaarheidsdatum: null,
      })
    }
  }
  return uit
}

function regelsVanPakketKoffer(k: LijstPakketKoffer): ControleLijstRegel[] {
  return k.regels.map((r, i) => ({
    sleutel: `PAKKET:${k.uniekItemId}:${r.pakketRegelId}`,
    bron: 'PAKKET_REGEL' as const,
    groep: 'Inhoud',
    ladeNummer: null,
    volgorde: i,
    omschrijving: r.naam,
    detail: null,
    aantal: r.aantal,
    serienummer: null,
    dragerInhoudRegelId: null,
    moduleOnderdeelId: null,
    uniekItemId: null,
    voorraadArtikelId: r.artikelId,
    pakketRegelId: r.pakketRegelId,
    opNaam: false,
    volgendeKeuring: null,
    houdbaarheidsdatum: null,
  }))
}

function ontbrekendeSet(soort: UitrustingSetSoort): ControleLijstSet {
  return {
    sleutel: `ONTBREEKT:${soort}`,
    soort,
    titel: UITRUSTING_SET_SOORT_LABELS[soort],
    nummer: null,
    bekendBijApp: false,
    dragerId: null,
    uniekItemId: null,
    uitgegevenOp: null,
    regels: [],
  }
}

/**
 * Het handgereedschap dat los op naam staat: een serviceset is in de praktijk geen
 * koffer met lades maar een reeks exemplaren en bulk op naam. Zonder deze set zag de
 * medewerker "nog niet aan jou uitgegeven" en kon hij niets per onderdeel melden.
 * Een exemplaar dat al in een lade van een kar of koffer zit, staat daar al.
 */
function losGereedschapSet(bezit: LijstBezit, dragers: readonly LijstDrager[]): ControleLijstSet | null {
  const inLade = new Set(dragers.flatMap((d) => d.regels.map((r) => r.losUniekItem?.id).filter(Boolean)))
  const exemplaren = bezit.uniekeItems
    .filter((u) => u.isLosGereedschap && !inLade.has(u.id))
    .sort((a, b) => NL_VOLGORDE(a.naam, b.naam) || NL_VOLGORDE(a.serienummer, b.serienummer))
  const bulk = bezit.bulk.filter((b) => b.isLosGereedschap && b.aantal > 0).sort((a, b) => NL_VOLGORDE(a.naam, b.naam))
  if (exemplaren.length === 0 && bulk.length === 0) return null
  const uitgifte = exemplaren
    .map((u) => u.uitgegevenOp)
    .filter((d): d is Date => d !== null)
    .sort((a, b) => a.getTime() - b.getTime())[0] ?? null
  return {
    sleutel: LOS_GEREEDSCHAP_SET_SLEUTEL,
    soort: 'SERVICEKOFFER',
    titel: UITRUSTING_SET_SOORT_LABELS.SERVICEKOFFER,
    nummer: null,
    bekendBijApp: true,
    dragerId: null,
    uniekItemId: null,
    uitgegevenOp: uitgifte,
    regels: [
      ...exemplaren.map((u, i) => ({
        sleutel: `UNIEK:${u.id}`,
        bron: 'UNIEK_ITEM' as const,
        groep: 'Gereedschap',
        ladeNummer: null,
        volgorde: i,
        omschrijving: u.naam,
        detail: exemplaarDetail(u),
        aantal: 1,
        serienummer: u.serienummer ?? u.assetTag,
        dragerInhoudRegelId: null,
        moduleOnderdeelId: null,
        uniekItemId: u.id,
        voorraadArtikelId: u.artikelId,
        pakketRegelId: null,
        opNaam: true,
        volgendeKeuring: u.volgendeKeuring,
        houdbaarheidsdatum: u.houdbaarheidsdatum,
      })),
      ...bulk.map((b, i) => ({
        sleutel: `BULK:${b.artikelId}`,
        bron: 'BULK' as const,
        groep: 'Gereedschap',
        ladeNummer: null,
        volgorde: exemplaren.length + i,
        omschrijving: b.naam,
        detail: null,
        aantal: b.aantal,
        serienummer: null,
        dragerInhoudRegelId: null,
        moduleOnderdeelId: null,
        uniekItemId: null,
        voorraadArtikelId: b.artikelId,
        pakketRegelId: null,
        opNaam: true,
        volgendeKeuring: null,
        houdbaarheidsdatum: null,
      })),
    ],
  }
}

/**
 * De afvinklijst van één medewerker, per gekozen setsoort in de volgorde van de
 * campagne. Exact wat de app van hem kent (spec T1): elke kar of koffer op zijn
 * naam met zijn lades (module-onderdelen uitgeklapt, bulk met aantal, een los
 * exemplaar met serienummer en keuring), FO-301-koffers zonder lades met de
 * pakketinhoud, het accugereedschap uit de exemplaren en de bulk op naam. Een
 * setsoort zonder uitgifte geeft één set "volgens de app nog niet aan jou
 * uitgegeven" (`bekendBijApp: false`, zonder regels).
 */
export function bouwControleLijst(
  bezit: LijstBezit,
  dragers: readonly LijstDrager[],
  setsoorten: readonly UitrustingSetSoort[],
): ControleLijstSet[] {
  const sets: ControleLijstSet[] = []
  const gezien = new Set<UitrustingSetSoort>()
  for (const soort of setsoorten) {
    if (gezien.has(soort)) continue
    gezien.add(soort)
    if (soort === 'ACCUGEREEDSCHAP') {
      const machines = bezit.uniekeItems
        .filter((u) => u.isAccu)
        .sort((a, b) => NL_VOLGORDE(a.naam, b.naam) || NL_VOLGORDE(a.serienummer, b.serienummer))
      const bulk = bezit.bulk.filter((b) => b.isAccu && b.aantal > 0).sort((a, b) => NL_VOLGORDE(a.naam, b.naam))
      if (machines.length === 0 && bulk.length === 0) {
        sets.push(ontbrekendeSet(soort))
        continue
      }
      const uitgifte = machines
        .map((m) => m.uitgegevenOp)
        .filter((d): d is Date => d !== null)
        .sort((a, b) => a.getTime() - b.getTime())[0] ?? null
      const regels: ControleLijstRegel[] = [
        ...machines.map((m, i) => ({
          sleutel: `UNIEK:${m.id}`,
          bron: 'UNIEK_ITEM' as const,
          groep: 'Machines',
          ladeNummer: null,
          volgorde: i,
          omschrijving: m.naam,
          detail: exemplaarDetail(m),
          aantal: 1,
          serienummer: m.serienummer ?? m.assetTag,
          dragerInhoudRegelId: null,
          moduleOnderdeelId: null,
          uniekItemId: m.id,
          voorraadArtikelId: m.artikelId,
          pakketRegelId: null,
          opNaam: true,
          volgendeKeuring: m.volgendeKeuring,
          houdbaarheidsdatum: m.houdbaarheidsdatum,
        })),
        ...bulk.map((b, i) => ({
          sleutel: `BULK:${b.artikelId}`,
          bron: 'BULK' as const,
          groep: "Accu's en laders",
          ladeNummer: null,
          volgorde: machines.length + i,
          omschrijving: b.naam,
          detail: null,
          aantal: b.aantal,
          serienummer: null,
          dragerInhoudRegelId: null,
          moduleOnderdeelId: null,
          uniekItemId: null,
          voorraadArtikelId: b.artikelId,
          pakketRegelId: null,
          // De netto bulk staat op zijn naam: een melding loopt eerst via het ledger
          // (integrator-review H1), anders boekte "aanvullen" de hele stand opnieuw uit.
          opNaam: true,
          volgendeKeuring: null,
          houdbaarheidsdatum: null,
        })),
      ]
      sets.push({
        sleutel: 'ACCU',
        soort,
        titel: UITRUSTING_SET_SOORT_LABELS[soort],
        nummer: null,
        bekendBijApp: true,
        dragerId: null,
        uniekItemId: null,
        uitgegevenOp: uitgifte,
        regels,
      })
      continue
    }

    const vanSoort = dragers
      .filter((d) => setSoortVanDrager(d) === soort)
      .sort((a, b) => NL_VOLGORDE(a.nummer, b.nummer) || a.dragerId.localeCompare(b.dragerId))
    const koffers = soort === 'SERVICEKOFFER'
      ? [...bezit.pakketKoffers].sort((a, b) => NL_VOLGORDE(a.nummer, b.nummer) || a.uniekItemId.localeCompare(b.uniekItemId))
      : []
    const losSet = soort === 'SERVICEKOFFER' ? losGereedschapSet(bezit, dragers) : null
    if (vanSoort.length === 0 && koffers.length === 0 && !losSet) {
      sets.push(ontbrekendeSet(soort))
      continue
    }
    for (const d of vanSoort) {
      sets.push({
        sleutel: `DRAGER:${d.dragerId}`,
        soort,
        titel: `${UITRUSTING_SET_SOORT_LABELS[soort]} ${d.nummer ?? d.naam}`,
        nummer: d.nummer,
        bekendBijApp: true,
        dragerId: d.dragerId,
        uniekItemId: d.uniekItemId,
        uitgegevenOp: d.uitgegevenOp,
        regels: regelsVanDrager(d),
      })
    }
    for (const k of koffers) {
      sets.push({
        sleutel: `KOFFER:${k.uniekItemId}`,
        soort,
        titel: `${UITRUSTING_SET_SOORT_LABELS[soort]} ${k.nummer ?? k.naam}`,
        nummer: k.nummer,
        bekendBijApp: true,
        dragerId: null,
        uniekItemId: k.uniekItemId,
        uitgegevenOp: k.uitgegevenOp,
        regels: regelsVanPakketKoffer(k),
      })
    }
    if (losSet) sets.push(losSet)
  }
  return sets
}

/** Heeft deze medewerker iets om te controleren (een bekende set met of zonder regels)? */
export function heeftUitrusting(sets: readonly Pick<ControleLijstSet, 'bekendBijApp'>[]): boolean {
  return sets.some((s) => s.bekendBijApp)
}

// ─── Keuring ────────────────────────────────────────────────────────────────────

const MS_PER_DAG = 86_400_000

/**
 * Keuring verlopen of binnen 60 dagen (§C5): het onderdeel krijgt het label
 * "keuren" en komt in de tab Keuringen. Neemt de vroegste van de volgende
 * keuring en de houdbaarheid.
 */
export function keuringSignaal(
  input: { volgendeKeuring: Date | null; houdbaarheidsdatum: Date | null },
  now: Date,
  dagen: number = UITRUSTING_KEURING_SIGNAAL_DAGEN,
): { keuren: boolean; tot: Date | null; verlopen: boolean; dagenTot: number | null } {
  const kandidaten = [input.volgendeKeuring, input.houdbaarheidsdatum].filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()))
  if (kandidaten.length === 0) return { keuren: false, tot: null, verlopen: false, dagenTot: null }
  const tot = kandidaten.sort((a, b) => a.getTime() - b.getTime())[0]!
  const dagenTot = Math.floor((tot.getTime() - now.getTime()) / MS_PER_DAG)
  return { keuren: dagenTot <= dagen, tot, verlopen: tot.getTime() < now.getTime(), dagenTot }
}

// ─── Statussen en mappings ──────────────────────────────────────────────────────

/** De melding van de medewerker als status op de inhoud-regel van de kar/koffer. */
export function regelStatusNaarDragerStatus(status: UitrustingControleRegelStatus): DragerInhoudStatusLike | null {
  switch (status) {
    case 'AANWEZIG':
      return 'AANWEZIG'
    case 'MIST':
      return 'ONTBREEKT'
    case 'DEFECT':
      return 'KAPOT'
    case 'NIET_ONTVANGEN':
      return 'NIET_ONTVANGEN'
    case 'OPEN':
      return null
    default: {
      const _x: never = status
      return _x
    }
  }
}

/** De melding als afwijking op een FO-301-koffer zonder lades (null = geen afwijking). */
export function regelStatusNaarAfwijkingType(status: UitrustingControleRegelStatus): 'ONTBREEKT' | 'DEFECT' | 'NIET_ONTVANGEN' | null {
  if (status === 'MIST') return 'ONTBREEKT'
  if (status === 'DEFECT') return 'DEFECT'
  if (status === 'NIET_ONTVANGEN') return 'NIET_ONTVANGEN'
  return null
}

/** Van meest naar minst ernstig: welke status een module-regel krijgt uit zijn onderdelen. */
const DRAGER_ERNST: readonly DragerInhoudStatusLike[] = [
  'ONTBREEKT',
  'NIET_ONTVANGEN',
  'KWIJT',
  'KAPOT',
  'VERSLETEN',
  'IN_BESTELLING',
  'VERVANGEN',
  'AANWEZIG',
  'NVT',
]

/** De ernstigste status van een reeks (een module met één ontbrekend onderdeel ontbreekt). */
export function ernstigsteDragerStatus(statussen: readonly DragerInhoudStatusLike[]): DragerInhoudStatusLike {
  let beste: DragerInhoudStatusLike = 'AANWEZIG'
  let besteIdx = DRAGER_ERNST.indexOf('AANWEZIG')
  for (const s of statussen) {
    const idx = DRAGER_ERNST.indexOf(s)
    if (idx >= 0 && idx < besteIdx) {
      besteIdx = idx
      beste = s
    }
  }
  return beste
}

/** Afhandelingen die een melding oplossen voor de compleet-regel (A5). */
export const UITRUSTING_AFHANDELING_OPGELOST: readonly UitrustingAfhandeling[] = ['AANGEVULD', 'VERVANGEN', 'NVT', 'HERSTELD']

export function isMeldingOpgelost(afhandeling: UitrustingAfhandeling | null): boolean {
  return afhandeling !== null && UITRUSTING_AFHANDELING_OPGELOST.includes(afhandeling)
}

// ─── Afhandelacties van het magazijn (§E12) ─────────────────────────────────────

const MELDING_STATUSSEN: readonly UitrustingControleRegelStatus[] = ['MIST', 'DEFECT', 'NIET_ONTVANGEN']

/** Acties die iets in of bij de set boeken (uitgeven, bestellen, reparatie). */
const BOEKENDE_ACTIES: readonly UitrustingOntbrekendActie[] = ['AANVULLEN', 'VERVANGEN', 'BESTELLEN', 'REPARATIE']

export interface AfhandelRegelContext {
  bron: UitrustingRegelBron | string
  status: UitrustingControleRegelStatus | string
  uniekItemId: string | null
  voorraadArtikelId: string | null
  opNaam: boolean
  afhandeling: UitrustingAfhandeling | null
  /** Een reparatieverzoek vraagt minstens één foto (van de medewerker of het magazijn). */
  fotoIds?: readonly string[]
  /** De set staat niet meer op naam (M1): niets meer in of bij deze set boeken. */
  bronVerdwenen?: boolean
  /** Van de koffer is niets als aanwezig bevestigd (`isKofferOnzeker`): eerst de koffer via het ledger. */
  kofferOnzeker?: boolean
}

/**
 * Welke acties zinvol zijn op een gemelde regel; scherm en API rekenen hiermee.
 *
 *  - T3 en integrator-review M2: een exemplaar op naam dat mist of niet ontvangen is,
 *    gaat eerst ALLEEN via "Zoeken in ledger"; bestellen of n.v.t. pas na het besluit
 *    (vermist), anders bleef het exemplaar stil op naam staan.
 *  - H1, bulk op naam (losse accu's en laders): de regel draagt de hele netto stand,
 *    niet het gemelde aantal. Eerst via het ledger een aantal afboeken (kwijt, kapot of
 *    nooit ontvangen); daarna vult of vervangt het magazijn precies dat aantal aan, of
 *    bestelt het. Nooit stil bijboeken.
 *  - H1, koffer onzeker: van de koffer is niets als aanwezig afgevinkt (ook niet via de
 *    QR). Dan is niet uitgezocht waar hij is: eerst de koffer via het ledger, pas daarna
 *    aanvullen of bestellen in die koffer.
 *  - M1, bron verdwenen: de set staat niet meer op naam; er wordt niets meer in of bij
 *    de set geboekt (wel nog het ledger en n.v.t.).
 */
export function uitrustingAfhandelActies(r: AfhandelRegelContext): UitrustingOntbrekendActie[] {
  if (!MELDING_STATUSSEN.includes(r.status as UitrustingControleRegelStatus)) return []
  if (isMeldingOpgelost(r.afhandeling)) return []
  const acties = basisAfhandelActies(r)
  return r.bronVerdwenen ? acties.filter((a) => !BOEKENDE_ACTIES.includes(a)) : acties
}

function basisAfhandelActies(r: AfhandelRegelContext): UitrustingOntbrekendActie[] {
  const heeftFoto = (r.fotoIds?.length ?? 0) > 0
  const defect = r.status === 'DEFECT'
  const acties: UitrustingOntbrekendActie[] = []

  if (r.bron === 'BULK') {
    if (r.afhandeling === null) {
      if (!defect) return ['LEDGER']
      acties.push('LEDGER')
      if (heeftFoto) acties.push('REPARATIE')
      acties.push('NVT')
      return acties
    }
    // Na de afboeking (VERMIST) of de bestelling: precies het afgeboekte aantal erbij.
    if (r.afhandeling === 'VERMIST' || r.afhandeling === 'BESTELD') {
      acties.push(defect ? 'VERVANGEN' : 'AANVULLEN')
      if (r.afhandeling === 'VERMIST') acties.push('BESTELLEN')
      acties.push('NVT')
      return acties
    }
    // In reparatie: lukt het niet, dan alsnog afboeken via het ledger.
    return ['LEDGER', 'NVT']
  }

  if (r.uniekItemId) {
    if (r.afhandeling === null && r.opNaam && !defect) return ['LEDGER']
    // Ook een exemplaar in een koffer (bron DRAGER_REGEL) kan in reparatie, met een foto.
    if (defect && heeftFoto && r.afhandeling !== 'REPARATIE') acties.push('REPARATIE')
    if (r.afhandeling !== 'BESTELD' && r.voorraadArtikelId) acties.push('BESTELLEN')
    acties.push('NVT')
    return acties
  }

  if (r.kofferOnzeker && r.afhandeling === null) return ['LEDGER']
  if (r.voorraadArtikelId) acties.push(defect ? 'VERVANGEN' : 'AANVULLEN')
  if (r.afhandeling !== 'BESTELD' && (r.voorraadArtikelId || r.bron === 'MODULE_ONDERDEEL')) acties.push('BESTELLEN')
  if (defect && heeftFoto && r.afhandeling !== 'REPARATIE') acties.push('REPARATIE')
  acties.push('NVT')
  return acties
}

/**
 * Is niet uitgezocht of de koffer (of kar) bij de medewerker is (integrator-review H1)?
 * Ja als er een koffer aan de set hangt, de bron nog op naam staat, de QR niet is
 * gescand en het magazijn hem niet heeft bevestigd, en van de inhoud niets als
 * aanwezig (of defect: dan heeft hij het wel) is afgevinkt terwijl er iets mist of
 * niet ontvangen is. Typisch: "Nooit gekregen? Zet de rest op niet ontvangen" op een
 * onaangeroerde set. Dan nooit iets in die koffer boeken voor het besluit.
 */
export function isKofferOnzeker(set: {
  uniekItemId: string | null
  bekendBijApp: boolean
  bronVerdwenen: boolean
  qrBevestigd: boolean
  kofferBevestigd: boolean
  regels: ReadonlyArray<{ status: UitrustingControleRegelStatus | string; vervallen: boolean }>
}): boolean {
  if (!set.uniekItemId || !set.bekendBijApp || set.bronVerdwenen || set.qrBevestigd || set.kofferBevestigd) return false
  const actief = set.regels.filter((r) => !r.vervallen)
  if (actief.some((r) => r.status === 'AANWEZIG' || r.status === 'DEFECT')) return false
  return actief.some((r) => r.status === 'MIST' || r.status === 'NIET_ONTVANGEN')
}

/** Minimale vorm van een set voor de regels hieronder (snapshot of DTO). */
export interface ControleSetVoorRegels {
  soort: UitrustingSetSoort
  titel: string
  bekendBijApp: boolean
  status: UitrustingControleSetStatus
  vervallen?: boolean
  regels: ReadonlyArray<{
    omschrijving: string
    status: UitrustingControleRegelStatus
    afhandeling: UitrustingAfhandeling | null
    vervallen?: boolean
  }>
}

export interface SetTellers {
  totaal: number
  open: number
  aanwezig: number
  mist: number
  defect: number
  nietOntvangen: number
}

export function telSetRegels(set: Pick<ControleSetVoorRegels, 'regels'>): SetTellers {
  const t: SetTellers = { totaal: 0, open: 0, aanwezig: 0, mist: 0, defect: 0, nietOntvangen: 0 }
  for (const r of set.regels) {
    if (r.vervallen) continue
    t.totaal += 1
    if (r.status === 'OPEN') t.open += 1
    // Een door het magazijn opgeloste melding (aangevuld, vervangen, NVT, klopt) telt als aanwezig.
    else if (r.status === 'AANWEZIG' || isMeldingOpgelost(r.afhandeling)) t.aanwezig += 1
    else if (r.status === 'MIST') t.mist += 1
    else if (r.status === 'DEFECT') t.defect += 1
    else if (r.status === 'NIET_ONTVANGEN') t.nietOntvangen += 1
  }
  return t
}

/** De pill per set in het overzicht (scherm 3): compleet, n mist, n defect, nog niet ontvangen. */
export function setSamenvatting(set: ControleSetVoorRegels): { tekst: string; toon: 'ok' | 'rood' | 'oranje' | 'neutraal' } {
  if (set.status === 'NIET_ONTVANGEN' || (!set.bekendBijApp && set.status !== 'WEL_IN_BEZIT')) {
    return { tekst: 'nog niet ontvangen', toon: 'oranje' }
  }
  if (set.status === 'WEL_IN_BEZIT') return { tekst: 'magazijn controleert', toon: 'oranje' }
  const t = telSetRegels(set)
  const delen: string[] = []
  if (t.mist > 0) delen.push(`${t.mist} mist`)
  if (t.defect > 0) delen.push(`${t.defect} defect`)
  if (t.nietOntvangen > 0) delen.push(`${t.nietOntvangen} niet ontvangen`)
  if (delen.length > 0) return { tekst: delen.join(', '), toon: 'rood' }
  if (t.open > 0) return { tekst: `nog ${t.open} af te vinken`, toon: 'neutraal' }
  return { tekst: 'compleet', toon: 'ok' }
}

/**
 * Kan de medewerker indienen (§6.1)? Elke regel heeft een status, of de set is
 * als "nog niet ontvangen" (of "wel in bezit" bij een onbekende set) gemarkeerd.
 * Geeft de titels van de sets terug die nog open regels hebben.
 */
export function setsNogOpen(sets: readonly ControleSetVoorRegels[]): string[] {
  const open: string[] = []
  for (const s of sets) {
    if (s.vervallen) continue
    if (s.status === 'NIET_ONTVANGEN' || s.status === 'WEL_IN_BEZIT') continue
    if (!s.bekendBijApp) {
      open.push(s.titel)
      continue
    }
    if (s.regels.some((r) => !r.vervallen && r.status === 'OPEN')) open.push(s.titel)
  }
  return open
}

/** Sets waarvoor de gereedschapsovereenkomst getekend wordt (A5): alle gereedschap- en PBM-sets. */
export const UITRUSTING_GEREEDSCHAP_SETS: readonly UitrustingSetSoort[] = ['SERVICEKOFFER', 'PBM_KOFFER', 'ACCUGEREEDSCHAP', 'KAR']

/**
 * De compleet-regel (§D10, A5): de gereedschapsovereenkomst (GEREEDSCHAP_SET) is
 * pas tekenbaar als de bijbehorende sets in de controle compleet zijn. Compleet
 * = elke regel aanwezig, of een melding die het magazijn heeft afgehandeld als
 * aangevuld, vervangen, niet van toepassing of hersteld. Een set die de
 * medewerker (nog) niet heeft, telt niet mee: daar tekent hij nog niet voor. Een
 * set die nog niet is afgevinkt houdt het tekenen tegen.
 *
 * `blokkerend` = de onderdelen die het tekenen tegenhouden ("momentsleutel mist").
 */
export function magGereedschapTekenen(
  sets: readonly ControleSetVoorRegels[],
  opties: { tekenenAlleenBijCompleet: boolean } = { tekenenAlleenBijCompleet: true },
): { mag: boolean; blokkerend: string[] } {
  if (!opties.tekenenAlleenBijCompleet) return { mag: true, blokkerend: [] }
  const blokkerend: string[] = []
  for (const s of sets) {
    if (s.vervallen || !UITRUSTING_GEREEDSCHAP_SETS.includes(s.soort)) continue
    if (!s.bekendBijApp) continue
    // Een set die op naam staat maar als nooit ontvangen is gemeld: eerst uitzoeken (T3), niet tekenen.
    if (s.status === 'NIET_ONTVANGEN' || s.status === 'WEL_IN_BEZIT') {
      blokkerend.push(`${s.titel} nog niet ontvangen`)
      continue
    }
    for (const r of s.regels) {
      if (r.vervallen) continue
      if (r.status === 'AANWEZIG') continue
      if (r.status === 'OPEN') {
        blokkerend.push(`${r.omschrijving} nog niet afgevinkt`)
        continue
      }
      if (isMeldingOpgelost(r.afhandeling)) continue
      const wat = r.status === 'MIST' ? 'mist' : r.status === 'DEFECT' ? 'defect' : 'niet ontvangen'
      blokkerend.push(`${r.omschrijving} ${wat}`)
    }
  }
  return { mag: blokkerend.length === 0, blokkerend }
}

/** Wat het magazijn krijgt, in gewone taal (scherm 3): per open melding en per set die nog moet komen. */
export function magazijnKrijgt(sets: readonly ControleSetVoorRegels[]): string[] {
  const uit: string[] = []
  for (const s of sets) {
    if (s.vervallen) continue
    const naam = s.titel.toLowerCase()
    if (s.status === 'NIET_ONTVANGEN' || (!s.bekendBijApp && s.status !== 'WEL_IN_BEZIT')) {
      uit.push(`${UITRUSTING_SET_SOORT_LABELS[s.soort].toLowerCase()} uitgeven`)
      continue
    }
    if (s.status === 'WEL_IN_BEZIT') {
      uit.push(`${UITRUSTING_SET_SOORT_LABELS[s.soort].toLowerCase()} op jouw naam zetten`)
      continue
    }
    for (const r of s.regels) {
      if (r.vervallen || isMeldingOpgelost(r.afhandeling)) continue
      if (r.status === 'MIST') uit.push(`${r.omschrijving.toLowerCase()} aanvullen (${naam})`)
      else if (r.status === 'DEFECT') uit.push(`${r.omschrijving.toLowerCase()} vervangen of repareren (${naam})`)
      else if (r.status === 'NIET_ONTVANGEN') uit.push(`${r.omschrijving.toLowerCase()} nakijken en uitgeven (${naam})`)
    }
  }
  return uit
}

// ─── Overeenkomst-weergave ──────────────────────────────────────────────────────

const NL_DATUM = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', day: '2-digit', month: '2-digit', year: 'numeric' })

/**
 * Hoe een overeenkomst in de wizard staat (§D9 + §D10). De bruikleenstatus
 * komt uit `MiddelBezitDTO.bruikleen` (of, voor kleding en PBM, uit de eigen
 * triggersoort); de compleet-regel geldt alleen voor de gereedschapsovereenkomst.
 */
export function overeenkomstWeergave(input: {
  soort: BruikleenTriggerSoort
  bruikleen: MiddelBruikleenStatus
  /** ISO-moment van de ondertekening (bij ONDERTEKEND). */
  getekendOp: string | null
  /** Er is een document (wacht op ondertekening of getekend). */
  documentId: string | null
  /** De medewerker meldde "klopt niet" en het magazijn heeft nog niet opnieuw opgesteld. */
  gemeld: boolean
  /** Uitkomst van `magGereedschapTekenen` (alleen relevant voor GEREEDSCHAP_SET). */
  gereedschap: { mag: boolean; blokkerend: string[] }
}): { status: UitrustingOvereenkomstStatus; actie: UitrustingOvereenkomstActie; subtekst: string } {
  if (input.bruikleen === 'ONDERTEKEND') {
    const op = input.getekendOp ? NL_DATUM.format(new Date(input.getekendOp)) : null
    return { status: 'GETEKEND', actie: input.documentId ? 'BEKIJKEN' : 'GEEN', subtekst: op ? `Getekend ${op}` : 'Getekend' }
  }
  if (input.bruikleen === 'GEEN' || input.bruikleen === 'BEEINDIGD') {
    if (input.soort === 'GEREEDSCHAP_SET' && !input.gereedschap.mag) {
      return { status: 'WACHT_OP_AANVULLING', actie: 'GEEN', subtekst: wachtTekst(input.gereedschap.blokkerend) }
    }
    return { status: 'GEEN', actie: 'VOORBEREIDEN', subtekst: 'Nog geen overeenkomst: laat hem nu opstellen' }
  }
  if (input.gemeld) {
    return { status: 'GEMELD_ONJUIST', actie: input.documentId ? 'BEKIJKEN' : 'GEEN', subtekst: 'Je meldde dat de lijst niet klopt; magazijn past hem aan' }
  }
  if (input.soort === 'GEREEDSCHAP_SET' && !input.gereedschap.mag) {
    return { status: 'WACHT_OP_AANVULLING', actie: input.documentId ? 'CONTROLEREN' : 'GEEN', subtekst: wachtTekst(input.gereedschap.blokkerend) }
  }
  if (input.bruikleen === 'OPEN_TAAK') {
    return { status: 'WORDT_OPGESTELD', actie: 'VOORBEREIDEN', subtekst: 'Wordt opgesteld met de actuele inhoud' }
  }
  return { status: 'TE_TEKENEN', actie: 'CONTROLEREN_EN_TEKENEN', subtekst: 'Nog niet getekend' }
}

function wachtTekst(blokkerend: readonly string[]): string {
  if (blokkerend.length === 0) return 'Tekenen na aanvulling'
  const eerste = blokkerend.slice(0, 2).join(', ')
  return `Tekenen na aanvulling: ${eerste}${blokkerend.length > 2 ? ` en nog ${blokkerend.length - 2}` : ''}`
}
